import { Component, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { IonItemSliding } from '@ionic/angular';

import { Subscription } from 'rxjs';

import { PlacePanel } from '@unpispas/upp-base';
import { PriceType } from '@unpispas/upp-defs';
import { ViewRenderer } from '@unpispas/upp-wdgt';

import { alertService } from '@unpispas/upp-base';
import { languageService } from '@unpispas/upp-base';
import { dataService } from '@unpispas/upp-data';
import { i18nSupport } from '@unpispas/upp-data';
import { viewService } from '@unpispas/upp-base';

import { TicketOfferView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';
import { OfferView } from '@unpispas/upp-data';
import { ProductView } from '@unpispas/upp-data';
import { DiscountView } from '@unpispas/upp-data';
import { PreselectView } from '@unpispas/upp-data';
import { TicketProductView } from '@unpispas/upp-data';
import { TicketDiscountView } from '@unpispas/upp-data';

export type TicketOfferStatus = 'AC' | 'PD' | 'PP' | null;

/****************************/
/* TICKET OFFER DISPLAY     */
/****************************/

export class TicketOfferDisplay {
    constructor(private ticketoffer: TicketOfferView, private _i18n: i18nSupport){
        // nothing to do
    }

    private get offer(): OfferView | null {
        return this.ticketoffer.offer;
    }

    /* access to calculated values */

    get ticket(): TicketView | null {
        return this.ticketoffer.ticket;
    }

    get photo() : string | null {
        return (this.offer?.photo) || null;
    }

    get avatar() : string | null {
        return (this.offer?.avatar) || null;
    }

    get name(): string | null {
        return this._i18n.tr(this.offer?.name ?? null);
    }

    get amount(): number {
        let _amount: number = this.ticketoffer.amount || 0;

        let _target = this.ticketoffer.DisplayFr;
        while (_target !== null){
            _amount += _target.amount || 0;
            _target = _target.DisplayFr;
        }

        return _amount;        
    }

    /**
     * Sums charge across the DisplayFr chain so grouped menus keep
     * unit price and line total coherent with amount aggregation.
     */
    private get totalcharge(): number {
        let _charge = this.ticketoffer.calcinfo?.charge || 0;

        let _target = this.ticketoffer.DisplayFr;
        while (_target !== null){
            _charge += _target.calcinfo?.charge || 0;
            _target = _target.DisplayFr;
        }

        return _charge;
    }

    get available(): boolean {
        return (this.offer?.IsAvailable) || false;
    }

    get price(): string | null {
        // check fixed price first
        const _totalfixed = this.totalfixed;
        if (_totalfixed != null){
            if (this.amount > 0){
                return (Math.round((Number(_totalfixed) / this.amount) * 100) / 100).toFixed(2);
            }

            return _totalfixed;
        }

        // get offer charge price (unit = total charge / aggregated amount)
        if (this.amount > 0){
            return (Math.round((this.totalcharge / this.amount) * 100) / 100).toFixed(2);
        }

        // get default offer price
        return (Math.round((this.ticketoffer.offer?.price || 0) * 100) / 100).toFixed(2);
    }

    private ticketitem(ticketproduct: TicketProductView): ProductView | PreselectView | null {
        const product = ticketproduct.product;
        if (!product){
            return null;
        }

        const selects: Array<PreselectView> = product.selects.slice(0).sort((a, b) => {
            const _aopts = a.options.length;
            const _bopts = b.options.length;

            return (_bopts - _aopts);   // sort by number of options desc
        });

        let shortcut = null;

        for (const select of selects){
            if (ticketproduct.IsDirect(select)){
                shortcut = select;  // this is the match with more options
            }

            if (shortcut){
                break;  // this is a shortcut
            }
        }

        return shortcut || product;
    }

    get products(): Array <OfferProductDisplay> {
        const _productmap = new Map <ProductView | PreselectView, number> ();

        let _ticketoffer: TicketOfferView | null = this.ticketoffer;
        while (_ticketoffer){
            for(const ticketproduct of _ticketoffer.products){
                const ticketitem: ProductView | PreselectView | null = this.ticketitem(ticketproduct);
                if (ticketitem){
                    _productmap.set(ticketitem, (_productmap.get(ticketitem) || 0) + (ticketproduct.amount || 0));
                }
            }  

            _ticketoffer = _ticketoffer.DisplayFr;  // include grouped ticketoffers
        }
        
        const _products: Array <OfferProductDisplay> = [];
        for(const [ item, amount ] of _productmap.entries()){
            _products.push(new OfferProductDisplay(item, amount, this._i18n))
        }
        return _products;
    }

    get isfixed(): boolean {
        return this.ticketoffer.fixed || false;
    }

    get onthehouse(): boolean {
        if (this.ticketoffer.calcinfo && this.ticketoffer.calcinfo.fixed){
            return (this.ticketoffer.calcinfo.fixed == 0);
        }

        return false;        
    }

    get totalprice(): string | null {
        const _totalfixed = this.totalfixed;
        if (_totalfixed != null){
            return _totalfixed;
        }

        return (Math.round(this.totalcharge * 100) / 100).toFixed(2);
    }

    get totalfixed(): string | null {
        if (this.ticketoffer.calcinfo?.fixed == null){
            return null;
        }

        // each grouped ticketoffer stores its own fixed total; sum the chain
        let _fixed = this.ticketoffer.calcinfo.fixed || 0;
        let _target = this.ticketoffer.DisplayFr;
        
        while (_target !== null){
            _fixed += _target.calcinfo?.fixed || 0;
            _target = _target.DisplayFr;
        }

        return (Math.round(_fixed * 100) / 100).toFixed(2);
    }

    get paidstatus(): TicketOfferStatus {
        return null;  // TODO
    }
}

/****************************/
/* OFFER PRODUCT DISPLAY    */
/****************************/

export class OfferProductDisplay {
    constructor(private ticketitem: ProductView | PreselectView, public amount: number, private _i18n: i18nSupport){
        // nothing to do
    }

    /* access to calculated values */

    get _uuid(): string | null {
        return this.ticketitem?._uuid || null;
    }

    get name(): string | null {
        return this._i18n.tr(this.ticketitem?.name ?? null);
    }

    get available(): boolean {
        return (this.ticketitem?.IsAvailable) || false;
    }
}

/****************************/
/* TICKET DISCOUNT DISPLAY  */
/****************************/

export class TicketDiscountDisplay {
    constructor(private ticketdiscount: TicketDiscountView, private _i18n: i18nSupport){
        // nothing to do
    }

    private get discount(): DiscountView | null {
        return this.ticketdiscount.discount;
    }

    get photo() : string | null {
        return (this.discount?.photo) || null;
    }

    get avatar() : string | null {
        return (this.discount?.avatar) || null;
    }

    get name(): string | null {
        return this._i18n.tr(this.discount?.name ?? null);
    }

    get description(): string | null {
        return this._i18n.tr(this.discount?.description ?? null);
    }

    get type(): string | null {
        return (this.discount?.type) || null;
    }

    get value(): number {
        return (this.discount?.value) || 0;
    }

    get chargeinfo(): PriceType | null {
        return this.ticketdiscount.chargeinfo;
    }

    get refundinfo(): PriceType | null {
        if (this.ticketdiscount.refundinfo.val == 0){
            return null;
        }

        return this.ticketdiscount.refundinfo;
    }
}

/****************************/
/* UPP-OFFER-TICKET         */
/****************************/

@Component({
    selector: 'upp-offer-ticket',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-ticket.html',
    styleUrls: [
      './upp-ticket.scss'
    ],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef]
    }]       
})
export class UppOfferTicketComponent extends i18nSupport implements OnChanges, OnDestroy {
    @Input() offer: TicketOfferView | null = null;          // when offer is applied to ticket
    @Input() discount: TicketDiscountView | null = null;    // when discount is applied to ticket
    @Input() mode: 'EDIT' | 'VIEW' = 'VIEW';                // edition mode (from ticket panel)

    @Output() ToPanel = new EventEmitter<PlacePanel>();
    @Output() Updated = new EventEmitter<void>();

    private _offer_subscription: Subscription | null = null;
    private _discount_subscription: Subscription | null = null;

    constructor(private lang: languageService, private change: ViewRenderer, private alertCtrl: alertService, private data: dataService, public view: viewService){
        super(lang, change.cdref);
    }    

    ngOnChanges(){
        if (this._offer_subscription){
            this._offer_subscription.unsubscribe();
            this._offer_subscription = null;
        }

        if (this.offer){
            this._offer_subscription = this.offer.OnViewRefresh.subscribe(
            () => {
                if (this.ofrdisplay || (this.dctdisplay)){
                    this.change.markForCheck();
                }
            });
        }

        if (this._discount_subscription){
            this._discount_subscription.unsubscribe();
            this._discount_subscription = null;
        }

        if (this.discount){
            this._discount_subscription = this.discount.OnViewRefresh.subscribe(
            () => {
                if (this.ofrdisplay || (this.dctdisplay)){
                    this.change.markForCheck();
                }
            });
        }
    }

    ngOnDestroy(): void {
        super.OnDestroy();

        if (this._offer_subscription){
            this._offer_subscription.unsubscribe();
            this._offer_subscription = null;
        }

        if (this._discount_subscription){
            this._discount_subscription.unsubscribe();
            this._discount_subscription = null;
        }
    }   
    
    /********************************/
    /* DISPLAY OPTIONS              */
    /********************************/    
    
    ProductTrack(index: number, product: OfferProductDisplay): any {
        return product?._uuid || null;
    }    

    private _ofrdisplay: TicketOfferDisplay | null = null; 
    private _dctdisplay: TicketDiscountDisplay | null = null; 

    get ofrdisplay() : TicketOfferDisplay | null {    
        if (this._ofrdisplay === null){
            if (this.offer != null){
                this._ofrdisplay = new TicketOfferDisplay(this.offer, this);
            }
        }

        return this._ofrdisplay;
    }

    get dctdisplay() : TicketDiscountDisplay | null {    
        if (this._dctdisplay === null){
            if (this.discount != null){
                this._dctdisplay = new TicketDiscountDisplay(this.discount, this);
            }
        }

        return this._dctdisplay;
    }
    
    private _showproducts = false;
    get ShowProducts(): boolean {
        return this._showproducts;
    }

    set ShowProducts(value: boolean){
        this._showproducts = value;
        this.change.markForCheck();
    }

    OnShowProducts(){
        this.ShowProducts = !this.ShowProducts;
    }

    EventColor(status: TicketOfferStatus){
        switch(status){
            case 'AC':
            case 'PP': return 'medium';  
            case 'PD': return 'success';
        }
        
        return 'dark';        
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/

    private _onaction = false;
    get IsSliding(){
        return this._onaction;
    }

    RemoveDiscount(event: Event, slider: IonItemSliding): void {
        event.stopPropagation();

        this._onaction = true;
        if (this.discount){
            this.discount.status = 'DE';
        }

        slider.close();
        setTimeout(() => {
            this._onaction = false;
            this.change.markForCheck();
        }, 600)

        this.Updated.emit();
    }
}